import { useTranslation } from 'react-i18next'
import { Users, ShoppingBag, UserPlus, CheckCircle2 } from 'lucide-react'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { KpiCard } from '@/components/kpi/KpiCard'
import { AnimatedNumber } from '@/components/kpi/AnimatedNumber'
import { MetricValue } from '@/components/kpi/MetricValue'
import { NotReady } from '@/components/kpi/NotReady'
import { Delta } from '@/components/kpi/Delta'
import { Sparkline } from '@/components/kpi/Sparkline'
import { formatNumber, formatPercent } from '@/lib/format'
import { RecentActivityFeed } from '@/components/activity/RecentActivityFeed'
import { MilestoneRail } from '@/components/milestones/MilestoneRail'
import { ServiceHealthStrip } from '@/components/services/ServiceHealthStrip'
import { ServiceAdoption } from '@/components/services/ServiceAdoption'

export function OverallSlide() {
  const { t } = useTranslation()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const locale = useSettingsStore((s) => s.locale)
  if (!snapshot) return null


  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label={t('kpi.activeClients')}
          icon={<Users className="size-4" />}
          value={
            <AnimatedNumber
              value={snapshot.clients.active}
              format={(n) => formatNumber(n, locale)}
            />
          }
          caption={`${formatNumber(snapshot.clients.newThisMonth, locale)} ${t(
            'common.thisMonth',
          ).toLowerCase()}`}
          footer={
            <>
              <Delta
                value={(snapshot.clients.newThisMonth / Math.max(1, snapshot.clients.active)) * 100}
              />
              <div className="h-9 w-24">
                <Sparkline values={snapshot.history.clients30d.map((p) => p.v)} tone="good" />
              </div>
            </>
          }
          tone="brand"
          emphasis
        />
        <KpiCard
          label={locale === 'cs' ? 'Objednávky dnes' : 'Orders today'}
          icon={<ShoppingBag className="size-4" />}
          value={
            <AnimatedNumber
              value={snapshot.ordersToday.reduce((sum, o) => sum + o.orders, 0)}
              format={(n) => formatNumber(n, locale)}
            />
          }
          caption={`${snapshot.ordersToday.length} ${
            locale === 'cs' ? 'poboček · od 4:00' : 'venues · since 4:00'
          }`}
          tone="good"
        />
        <KpiCard
          label={locale === 'cs' ? 'Noví klienti za 14 dní' : 'New clients (14 days)'}
          icon={<UserPlus className="size-4" />}
          value={
            <AnimatedNumber
              value={snapshot.clients.newLast14d}
              format={(n) => formatNumber(n, locale)}
            />
          }
          caption={
            locale === 'cs' ? 'za posledních 14 dní' : 'in the last 14 days'
          }
          tone="good"
        />
        <NotReady>
          <KpiCard
            label={locale === 'cs' ? 'Chybovost synchronizace' : 'Sync error rate'}
            icon={<CheckCircle2 className="size-4" />}
            value={
              <MetricValue
                value={snapshot.syncRatio.errorPct}
                format={(n) => formatPercent(n, locale, 1)}
              />
            }
            caption={`${formatNumber(snapshot.syncRatio.success, locale)} ${
              locale === 'cs' ? 'úspěchů' : 'succeeded'
            } · ${formatNumber(snapshot.syncRatio.error, locale)} ${
              locale === 'cs' ? 'chyb za 30 dní' : 'failed in 30 days'
            }`}
            tone={
              snapshot.syncRatio.errorPct != null && snapshot.syncRatio.errorPct > 10
                ? 'warn'
                : 'good'
            }
          />
        </NotReady>
      </div>

      <ServiceHealthStrip services={snapshot.serviceHealth} />

      {/* Feed dostal celou výšku po grafu příjmu, který je duplicitně
          na slidu Obchod & finance. */}
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        <div className="col-span-6 min-h-0">
          <RecentActivityFeed />
        </div>
        <section className="col-span-3 flex min-h-0 flex-col rounded-3xl border border-border bg-surface-1/70 p-5">
          <h3 className="mb-3 shrink-0 text-xs uppercase tracking-[0.18em] text-ink-subtle">
            {locale === 'cs' ? 'Nasazení služeb' : 'Service adoption'}
          </h3>
          <div className="min-h-0 flex-1 overflow-hidden">
            <ServiceAdoption rows={snapshot.serviceAdoption} totalVenues={snapshot.clients.total} />
          </div>
        </section>
        <div className="col-span-3 flex min-h-0 flex-col gap-4">
          <MilestoneRail />
        </div>
      </div>
    </div>
  )
}
